import { InformationOutline as InformationOutlineIcon } from 'mdi-material-ui';
import React from 'react';
import { styled } from '../../utils';
import { CollapsibleIconButton } from '../Collapsible/CollapsibleIconButton';
import { Markdown } from '../Markdown';
import { Stack } from '../Stack';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { InternalContainerActions } from './ContainerActions';
import { useContainerContext } from './ContainerContextProvider';
import { ContainerHeaderProps } from './model';
import { useContainerLayout } from './useContainerLayout';

const StyledMarkdown = styled(Markdown)({
  'p:last-child': {
    marginBottom: 0,
  },
});

export const ContainerHeader = ({
  children,
  description,
  endAdornment,
  Icon,
  variant = 'strong',
  actions: deprecatedActions,
  'data-testid': dataTestId,
  'data-trackid': dataTrackId,
}: ContainerHeaderProps) => {
  const {
    onCollapseButtonClick,
    collapsed,
    subtleActionsVisibility,
    isCollapsible,
    collapseButtonAriaLabel,
    disablePadding,
  } = useContainerContext();

  const actionsVisibility = variant === 'strong' ? 'always' : subtleActionsVisibility || 'always';

  const { header: headerLayout } = useContainerLayout({ disablePadding, collapsed, variant });

  return (
    <Stack
      direction="row"
      sx={{
        minHeight: `${headerLayout.height}px`,

        // Calc-based width ensures this behaves properly in the disablePadding use case where margins are used to
        // add back spacing we lose from the Container parent.
        width: `calc(100% - ${headerLayout.marginX}px * 2)`,
        marginX: `${headerLayout.marginX}px`,
        marginTop: `${headerLayout.marginTop}px`,
        marginBottom: `${headerLayout.marginBottom}px`,
      }}
      justifyContent="space-between"
      spacing={1}
      className="container-header"
      data-testid={dataTestId}
      data-trackid={dataTrackId}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        className="container-title"
        alignSelf="flex-start"
        sx={{ paddingTop: 0.5, flex: 1, width: 0, cursor: isCollapsible ? 'pointer' : undefined }}
        onClick={
          isCollapsible
            ? () => {
                onCollapseButtonClick?.(!collapsed);
              }
            : undefined
        }
      >
        {isCollapsible && (
          <CollapsibleIconButton
            collapsed={collapsed}
            ariaLabelSubject={collapseButtonAriaLabel ?? 'container'}
            noPadding
            size="medium"
          />
        )}
        {Icon && <Icon sx={{ fontSize: '16px' }} />}
        <Typography
          variant="h3"
          noWrap
          sx={(theme) => ({
            fontSize: variant === 'subtle' ? theme.typography.h4.fontSize : undefined,
            fontWeight: variant === 'subtle' ? theme.typography.fontWeightRegular : undefined,
          })}
        >
          {children}
        </Typography>
        {/* Show the info icon when description is defined and is not all whitespace */}
        {description !== undefined && description.trim().length > 0 && (
          <Tooltip Content={<StyledMarkdown>{description}</StyledMarkdown>}>
            <InformationOutlineIcon
              aria-label={description}
              aria-describedby="info-icon"
              aria-hidden={false}
              fontSize="small"
              sx={{ color: (theme) => theme.palette.text.secondary, width: '20px' }}
              tabIndex={0}
            />
          </Tooltip>
        )}
        {endAdornment}
      </Stack>
      <InternalContainerActions actionsVisibility={actionsVisibility} deprecatedActions={deprecatedActions} />
    </Stack>
  );
};
